import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import PageLayout from "@/components/feature/PageLayout";
import MovieCard from "@/components/feature/MovieCard";
import FilterPanel, { type CategoryFilters } from "@/pages/category/components/FilterPanel";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;

const sortOptions = [
  { label: "按更新时间", value: "updated" },
  { label: "按热度", value: "views" },
  { label: "按评分", value: "rating" },
];

const defaultFilters: CategoryFilters = {
  type: "全部",
  region: "全部",
  genre: "全部",
  year: "all",
};

export default function Category() {
  usePageMeta(
    "影视分类 - 诱人的女员工hd影视专区",
    "影视分类页支持按电影、电视剧、地区、题材与年份多条件筛选，并提供更新时间、热度与评分排序，方便快速找到想看的影视作品。",
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const urlGenre = searchParams.get("genre") ?? "全部";
  const [filters, setFilters] = useState<CategoryFilters>({ ...defaultFilters, genre: urlGenre });
  const [sortBy, setSortBy] = useState("updated");
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, genre: urlGenre }));
    setPage(1);
  }, [urlGenre]);

  const filtered = useMemo(() => {
    const list = movies.filter((movie) => {
      if (filters.type !== "全部" && movie.type !== filters.type) return false;
      if (filters.region !== "全部" && movie.region !== filters.region) return false;
      if (filters.genre !== "全部" && !movie.genres.includes(filters.genre)) return false;
      if (filters.year === "earlier" && movie.year > 2022) return false;
      if (filters.year !== "all" && filters.year !== "earlier" && String(movie.year) !== filters.year) return false;
      return true;
    });

    return list.sort((a, b) => {
      if (sortBy === "rating") return b.rating - a.rating;
      if (sortBy === "views") return b.views - a.views;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
  }, [filters, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleFilterChange = (key: keyof CategoryFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    if (key === "genre") {
      const next = new URLSearchParams(searchParams);
      if (value === "全部") next.delete("genre");
      else next.set("genre", value);
      setSearchParams(next, { replace: true });
    }
  };

  const handleReset = () => {
    setFilters({ ...defaultFilters });
    setSortBy("updated");
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  return (
    <PageLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap">
              首页
            </Link>
            <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center text-foreground-300" />
            <span className="text-foreground-700">影视分类</span>
          </nav>
          <h1 className="mt-4 text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">影视分类</h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
            按类型、地区、题材与年份组合筛选，快速缩小范围。选择条件后列表会即时刷新，你也可以随时重置回全部作品。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          onReset={handleReset}
          resultCount={filtered.length}
          open={panelOpen}
          onToggle={() => setPanelOpen((open) => !open)}
        />

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-foreground-600">
            共 <span className="font-medium text-foreground-950">{filtered.length}</span> 部作品
            {filters.genre !== "全部" && <span className="text-foreground-500"> · 题材：{filters.genre}</span>}
          </p>

          <div className="flex w-full items-center gap-1 rounded-full bg-background-100 p-1 sm:w-auto">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setSortBy(option.value);
                  setPage(1);
                }}
                className={`flex-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors cursor-pointer sm:flex-none ${
                  sortBy === option.value
                    ? "bg-primary-950 text-background-50 font-medium"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {paged.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4 xl:grid-cols-6">
            {paged.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <h2 className="mt-4 text-base font-medium text-foreground-950">无匹配内容</h2>
            <p className="mt-2 max-w-sm text-sm leading-7 text-foreground-500">
              当前筛选条件下暂时没有作品，试着放宽年份或题材条件，或者直接重置全部筛选。
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line w-4 h-4 flex items-center justify-center" />
              重置筛选条件
            </button>
          </div>
        )}

        {filtered.length > 0 && (
          <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-background-200 pt-6 sm:flex-row">
            <p className="text-xs text-foreground-500">
              第 {currentPage} / {totalPages} 页 · 每页 {PAGE_SIZE} 部
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-xs text-foreground-700 transition-colors enabled:hover:border-accent-400 enabled:hover:text-accent-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center" />
                上一页
              </button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  onClick={() => setPage(pageNumber)}
                  aria-current={pageNumber === currentPage ? "page" : undefined}
                  className={`flex h-9 w-9 items-center justify-center rounded-md font-label text-xs transition-colors cursor-pointer ${
                    pageNumber === currentPage
                      ? "bg-primary-950 text-background-50"
                      : "border border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-600"
                  }`}
                >
                  {pageNumber}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-xs text-foreground-700 transition-colors enabled:hover:border-accent-400 enabled:hover:text-accent-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer whitespace-nowrap"
              >
                下一页
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
              </button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}